import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

/**
 * The one confirmation dialog for destructive, bulk or irreversible actions.
 *
 * <ConfirmDialog open={open} onOpenChange={setOpen}
 *   title="Delete 48,210 sales records for IN?"
 *   description="This cannot be undone."
 *   confirmLabel="Delete sales data" destructive
 *   typeToConfirm="IN"            // optional: user must type this exact text
 *   onConfirm={async () => {...}} // may be async; dialog shows a busy state and closes on success
 * >optional extra content (summary table, counts)</ConfirmDialog>
 *
 * If onConfirm throws, the dialog stays open so the caller's error toast is seen in context.
 */
export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  busyLabel,
  destructive = false,
  typeToConfirm,
  onConfirm,
  testId = 'confirm-dialog',
}) => {
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState('');
  useEffect(() => { if (open) setTyped(''); }, [open]);

  const blocked = busy || (typeToConfirm && typed.trim() !== typeToConfirm);
  const confirm = async () => {
    if (blocked) return;
    setBusy(true);
    try {
      await onConfirm?.();
      onOpenChange(false);
    } catch {
      // Caller reports the error; keep the dialog open.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!busy) onOpenChange(value); }}>
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        {typeToConfirm && (
          <div className="space-y-1.5">
            <label htmlFor={`${testId}-type`} className="text-sm text-gray-700">
              Type <span className="font-mono font-semibold">{typeToConfirm}</span> to confirm
            </label>
            <Input
              id={`${testId}-type`}
              value={typed}
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}
              data-testid={`${testId}-type-input`}
            />
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)} data-testid={`${testId}-cancel`}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'destructive' : 'default'} disabled={blocked} onClick={confirm} data-testid={`${testId}-confirm`}>
            {busy ? (busyLabel || 'Working…') : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
